<template>
    <div class="task-trace-viewer">
        <!-- Loading state -->
        <div v-if="task && task.loading" class="loading-container">
            <div class="loading-content">
                <i class="material-icons rotating">refresh</i>
                <p>Task detayları yükleniyor...</p>
            </div>
        </div>
        
        <!-- Header bilgileri -->
        <div v-else class="trace-header">
            <div class="task-info">
                <div class="header-top">
                    <h4>
                        <i class="material-icons">timeline</i>
                        Task Trace & Review
                    </h4>
                    <div class="view-toggle">
                        <a-button-group>
                            <a-button 
                                :type="viewMode === 'list' ? 'primary' : 'default'"
                                @click="viewMode = 'list'"
                                size="small"
                            >
                                <i class="material-icons">list</i>
                                Liste
                            </a-button>
                            <a-button 
                                :type="viewMode === 'diagram' ? 'primary' : 'default'"
                                @click="viewMode = 'diagram'"
                                size="small"
                            >
                                <i class="material-icons">account_tree</i>
                                Harita
                            </a-button>
                        </a-button-group>
                    </div>
                </div>
                <div class="task-meta">
                    <span class="meta-item">
                        <strong>Task ID:</strong> {{ task._id }}
                    </span>
                    <span class="meta-item">
                        <strong>Status:</strong> 
                        <span class="status-badge" :data-status="task.status">{{ task.status }}</span>
                    </span>
                    <span class="meta-item">
                        <strong>Duration:</strong> {{ formatDuration($_.get(task, 'state.duration', 0)) }}
                    </span>
                    <span class="meta-item">
                        <strong>Wake Up Count:</strong> {{ $_.get(task, 'state.wakeUpCount', 0) }}
                    </span>
                </div>
            </div>
        </div>

        <!-- Task trace timeline (Liste görünümü) -->
        <div v-if="viewMode === 'list'" class="trace-timeline">
            <div class="timeline-header">
                <h5>
                    <i class="material-icons">account_tree</i>
                    Block Execution History
                </h5>
            </div>
            
            <div class="timeline-container">
                <div 
                    v-for="(blockId, index) in executionHistory" 
                    :key="`${blockId}-${index}`"
                    class="timeline-item"
                    :class="{ 'current-position': blockId === currentPosition }"
                >
                    <div class="timeline-marker">
                        <span class="step-number">{{ index + 1 }}</span>
                    </div>
                    <div class="timeline-content">
                        <div class="block-header">
                            <div class="block-info">
                                <h6>{{ getBlockName(blockId) }}</h6>
                                <span class="block-id">ID: {{ blockId }}</span>
                            </div>
                            <a-button 
                                size="small" 
                                type="default" 
                                @click="openDiagramModal(blockId)"
                                class="diagram-btn"
                                title="Diagram'da konumunu göster"
                            >
                                <i class="material-icons">map</i>
                            </a-button>
                        </div>
                        
                        <!-- Block değeri varsa göster -->
                        <div v-if="getBlockValue(blockId)" class="block-value">
                            <div class="value-header">
                                <span class="value-label">Block Value:</span>
                                <a-button 
                                    size="small" 
                                    type="primary" 
                                    @click="openBlockValueModal(blockId)"
                                    class="view-value-btn"
                                >
                                    <i class="material-icons">code</i>
                                    Değeri Görüntüle
                                </a-button>
                            </div>
                        </div>
                        
                        <!-- Block değeri yoksa -->
                        <div v-else class="no-value">
                            <span class="no-value-text">Bu block için değer kaydedilmemiş</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Diagram görünümü -->
        <div v-if="viewMode === 'diagram'" class="trace-diagram" :class="{ 'fullscreen': diagramFullscreen }">
            <LiteDiagramViewer
                :diagram="diagramSnapshot"
                :highlighted-blocks="highlightedBlocks"
                :execution-history="executionHistory"
                :current-position="currentPosition"
                :block-values="blockValues"
                :show-payload-button="true"
                :fullscreen-mode="diagramFullscreen"
                @blockClicked="handleDiagramBlockClick"
                @showPayload="openPayloadModalFromDiagram"
                @toggleFullscreen="toggleDiagramFullscreen"
            />
        </div>

        <!-- Custom Attributes -->
        <div v-if="customAttributes && Object.keys(customAttributes).length > 0" class="custom-attributes">
            <div class="attributes-header">
                <h5>
                    <i class="material-icons">settings</i>
                    Custom Attributes
                </h5>
                <a-button 
                    size="small" 
                    type="link" 
                    @click="toggleCustomAttributes"
                    class="toggle-btn"
                >
                    {{ showCustomAttributes ? 'Gizle' : 'Göster' }}
                    <i class="material-icons">
                        {{ showCustomAttributes ? 'expand_less' : 'expand_more' }}
                    </i>
                </a-button>
            </div>
            
            <div v-if="showCustomAttributes" class="attributes-content">
                <div class="json-viewer-container">
                    <v-jsoneditor
                        :value="customAttributes"
                        :options="jsonEditorOptions"
                        height="300px"
                    ></v-jsoneditor>
                </div>
            </div>
        </div>

        <!-- Terminate Reason -->
        <div v-if="task.terminateReason" class="terminate-reason">
            <div class="reason-header">
                <h5>
                    <i class="material-icons">report_problem</i>
                    Terminate Reason
                </h5>
            </div>
            <div class="reason-content">
                <span class="reason-text">{{ task.terminateReason }}</span>
            </div>
        </div>

        <!-- Block Value Modal -->
        <Modal2
            v-if="showBlockValueModal"
            @cancel="closeBlockValueModal"
            :title="blockValueModalTitle"
            :showFooter="false"
            xlarge
        >
            <div class="block-value-modal-content">
                <v-jsoneditor
                    v-model="selectedBlockValue"
                    :options="modalJsonEditorOptions"
                    height="500px"
                ></v-jsoneditor>
            </div>
        </Modal2>

        <!-- Diagram Position Modal -->
        <Modal2
            v-if="showDiagramModal"
            @cancel="closeDiagramModal"
            title="Block Konum Görünümü"
            :showFooter="false"
            large
        >
            <div class="diagram-modal-content">
                <LiteDiagramViewer
                    :diagram="diagramSnapshot"
                    :highlighted-blocks="[selectedBlockForDiagram]"
                    :execution-history="executionHistory"
                    :current-position="currentPosition"
                    :block-values="blockValues"
                    :show-payload-button="true"
                    :fullscreen-mode="false"
                    @blockClicked="handleDiagramBlockClick"
                    @showPayload="openPayloadModalFromDiagram"
                />
            </div>
        </Modal2>
    </div>
</template>

<script>
import VJsoneditor from "v-jsoneditor";
import moment from "moment";
import Modal2 from "../../../../../../components/common/Modal2.vue";
import LiteDiagramViewer from "./LiteDiagramViewer.vue";

export default {
    name: "TaskTraceViewer",
    components: {
        VJsoneditor,
        Modal2,
        LiteDiagramViewer,
    },
    props: {
        task: {
            type: Object,
            required: true,
        },
        plan: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            viewMode: 'diagram', // 'list' or 'diagram' - default harita
            diagramFullscreen: false,
            expandedBlocks: [],
            showCustomAttributes: false,
            showBlockValueModal: false,
            selectedBlockValue: null,
            blockValueModalTitle: "",
            showDiagramModal: false,
            selectedBlockForDiagram: null,
            jsonEditorOptions: {
                mode: "tree",
                modes: ["tree", "code", "view"],
                navigationBar: true,
                mainMenuBar: false,
                statusBar: false,
                language: "tr",
                search: true,
                expandAll: false,
            },
            modalJsonEditorOptions: {
                mode: "tree",
                modes: ["tree", "code", "view"],
                navigationBar: true,
                mainMenuBar: true,
                statusBar: true,
                language: "tr",
                search: true,
                expandAll: true, // Modal'da tüm açık
            },
        };
    },
    computed: {
        executionHistory() {
            return this.task.state?.history || [];
        },
        currentPosition() {
            return this.task.state?.position;
        },
        blockValues() {
            return this.task.state?.blockValues || {};
        },
        customAttributes() {
            return this.task.state?.customAttributes || {};
        },
        diagramSnapshot() {
            return this.task.diagramSnapshot || this.plan.automation?.diagram || {};
        },
        flatDiagram() {
            // Diagram'ı flatten etmek için basit bir fonksiyon
            const flatten = (node, path = "", result = []) => {
                if (!node) return result;
                
                result.push({
                    id: node.id,
                    name: node.name,
                    type: node.type,
                    path: path,
                });
                
                if (node.children && Array.isArray(node.children)) {
                    node.children.forEach((child, index) => {
                        flatten(child, path ? `${path}.children[${index}]` : `children[${index}]`, result);
                    });
                }
                
                return result;
            };
            
            return flatten(this.diagramSnapshot);
        },
        highlightedBlocks() {
            // Şu anda modal'da gösterilen block'u highlight et
            return this.selectedBlockForDiagram ? [this.selectedBlockForDiagram] : [];
        },
    },
    methods: {
        getBlockName(blockId) {
            // Diagram'dan block bilgisini bul
            const block = this.flatDiagram.find(b => b.id === blockId);
            if (block) {
                if (block.name && block.name.trim()) {
                    return block.name;
                } else if (block.type) {
                    return this.getBlockTypeDisplayName(block.type);
                }
            }
            return `Block ${blockId.slice(-8)}`;
        },
        getBlockTypeDisplayName(type) {
            const typeNames = {
                'START_POINT': 'Başlangıç Noktası',
                'BOT_RESPONSE': 'Bot Yanıtı',
                'WEBHOOK': 'Webhook',
                'SLEEP': 'Bekleme',
                'CREATE_CONTACT': 'Kişi Oluştur',
                'UPDATE_CONTACT': 'Kişi Güncelle',
                'SEND_EMAIL': 'E-posta Gönder',
                'SEND_SMS': 'SMS Gönder',
                'ASK_AI': 'AI Sor',
                'FILTER': 'Filtre',
                'LOOP': 'Döngü',
                'TERMINATE_AUTOMATION': 'Otomasyonu Sonlandır',
                'DEFAULT_FALLBACK': 'Varsayılan Fallback',
                'SUCCESS': 'Başarılı',
                'FAILURE': 'Başarısız',
                'TIMEOUT': 'Zaman Aşımı',
            };
            return typeNames[type] || type;
        },
        getBlockValue(blockId) {
            return this.blockValues[blockId] || null;
        },
        toggleBlockValue(blockId) {
            const index = this.expandedBlocks.indexOf(blockId);
            if (index > -1) {
                this.expandedBlocks.splice(index, 1);
            } else {
                this.expandedBlocks.push(blockId);
            }
        },
        toggleCustomAttributes() {
            this.showCustomAttributes = !this.showCustomAttributes;
        },
        formatDuration(seconds) {
            if (!seconds) return "0s";
            
            const duration = moment.duration(seconds, "seconds");
            const hours = Math.floor(duration.asHours());
            const minutes = duration.minutes();
            const secs = duration.seconds();
            
            if (hours > 0) {
                return `${hours}h ${minutes}m ${secs}s`;
            } else if (minutes > 0) {
                return `${minutes}m ${secs}s`;
            } else {
                return `${secs}s`;
            }
        },
        openBlockValueModal(blockId) {
            const blockValue = this.getBlockValue(blockId);
            const blockName = this.getBlockName(blockId);
            this.selectedBlockValue = blockValue;
            this.blockValueModalTitle = `Block Value - ${blockName}`;
            this.showBlockValueModal = true;
        },
        closeBlockValueModal() {
            this.showBlockValueModal = false;
            this.selectedBlockValue = null;
            this.blockValueModalTitle = "";
        },
        openDiagramModal(blockId) {
            this.selectedBlockForDiagram = blockId;
            this.showDiagramModal = true;
        },
        closeDiagramModal() {
            this.showDiagramModal = false;
            this.selectedBlockForDiagram = null;
        },
        handleDiagramBlockClick(blockData) {
            if (blockData.hasValue) {
                this.openBlockValueModal(blockData.blockId);
            } else {
                this.$message.info(`${blockData.blockName} - Bu block için değer kaydedilmemiş`);
            }
        },
        openPayloadModalFromDiagram() {
            // Plan payload'ını göster - parent component'e emit et
            this.$emit('showPayload');
        },
        toggleDiagramFullscreen() {
            this.diagramFullscreen = !this.diagramFullscreen;
        },
    },
};
</script>

<style lang="scss" scoped>
.task-trace-viewer {
    padding: 20px;
    max-height: 70vh;
    overflow-y: auto;
}

.trace-header {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--color-border-default);

    .task-info {
        .header-top {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;

            h4 {
                display: flex;
                align-items: center;
                gap: 8px;
                margin: 0;
                color: var(--color-text-primary);
                font-size: 18px;

                .material-icons {
                    color: var(--color-primary);
                }
            }

            .view-toggle {
                .ant-btn-group {
                    .ant-btn {
                        display: flex;
                        align-items: center;
                        gap: 4px;
                        
                        .material-icons {
                            font-size: 16px;
                        }
                    }
                }
            }
        }

        .task-meta {
            display: flex;
            flex-wrap: wrap;
            gap: 16px;

            .meta-item {
                font-size: 14px;
                color: var(--color-text-secondary);

                strong {
                    color: var(--color-text-primary);
                }

                .status-badge {
                    padding: 2px 8px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 600;
                    text-transform: uppercase;

                    &[data-status="completed"],
                    &[data-status="done"] {
                        background: rgb(222, 243, 233);
                        color: rgb(45, 46, 46);
                    }

                    &[data-status="running"] {
                        background: rgb(255, 244, 188);
                        color: rgb(45, 46, 46);
                    }

                    &[data-status="failed"],
                    &[data-status="cancelled"] {
                        background: rgb(250, 217, 239);
                        color: rgb(45, 46, 46);
                    }

                    &[data-status="scheduled"] {
                        background: rgb(255, 244, 188);
                        color: rgb(45, 46, 46);
                    }
                }
            }
        }
    }
}

.trace-timeline {
    margin-bottom: 24px;

    .timeline-header {
        margin-bottom: 16px;

        h5 {
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0;
            color: var(--color-text-primary);
            font-size: 16px;

            .material-icons {
                color: var(--color-primary);
            }
        }
    }

    .timeline-container {
        position: relative;
        padding-left: 40px;

        &::before {
            content: '';
            position: absolute;
            left: 20px;
            top: 0;
            bottom: 0;
            width: 2px;
            background: var(--color-border-default);
        }

        .timeline-item {
            position: relative;
            margin-bottom: 24px;

            &.current-position {
                .timeline-marker {
                    .step-number {
                        background: var(--color-primary);
                        color: white;
                    }
                }

                .timeline-content {
                    border-left: 3px solid var(--color-primary);
                    background: var(--color-bg-subtle);
                }
            }

            .timeline-marker {
                position: absolute;
                left: -30px;
                top: 8px;
                width: 40px;
                height: 40px;
                display: flex;
                align-items: center;
                justify-content: center;

                .step-number {
                    width: 24px;
                    height: 24px;
                    border-radius: 50%;
                    background: var(--color-bg-default);
                    border: 2px solid var(--color-border-default);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    font-weight: 600;
                    color: var(--color-text-secondary);
                }
            }

            .timeline-content {
                background: var(--color-bg-default);
                border: 1px solid var(--color-border-default);
                border-radius: 8px;
                padding: 16px;

                .block-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 12px;

                    .block-info {
                        flex: 1;

                        h6 {
                            margin: 0 0 4px 0;
                            color: var(--color-text-primary);
                            font-size: 14px;
                            font-weight: 600;
                        }

                        .block-id {
                            font-size: 12px;
                            color: var(--color-text-disabled);
                            font-family: 'Courier New', monospace;
                        }
                    }

                    .diagram-btn {
                        height: 28px;
                        width: 28px;
                        padding: 0;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 6px;

                        .material-icons {
                            font-size: 16px;
                        }
                    }
                }

                .block-value {
                    .value-header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        margin-bottom: 8px;

                        .value-label {
                            font-size: 13px;
                            font-weight: 600;
                            color: var(--color-text-secondary);
                        }

                        .view-value-btn {
                            padding: 4px 12px;
                            height: auto;
                            font-size: 12px;
                            
                            .material-icons {
                                font-size: 14px;
                                margin-right: 4px;
                            }
                        }
                    }

                    .value-content {
                        .json-viewer-container {
                            border-radius: 4px;
                            overflow: hidden;
                            
                            .jsoneditor-container {
                                border: 1px solid var(--color-border-default);
                            }
                        }
                    }
                }

                .no-value {
                    .no-value-text {
                        font-size: 13px;
                        color: var(--color-text-disabled);
                        font-style: italic;
                    }
                }
            }
        }
    }
}

.trace-diagram {
    height: 500px;
    border: 1px solid var(--color-border-default);
    border-radius: 8px;
    overflow: hidden;
    
    &.fullscreen {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        z-index: 9999 !important;
        background: white !important;
        border: none !important;
        border-radius: 0 !important;
        padding: 20px;
        box-sizing: border-box;
    }
}

.custom-attributes {
    margin-bottom: 24px;
    padding: 16px;
    background: var(--color-bg-subtle);
    border-radius: 8px;

    .attributes-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 16px;

        h5 {
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0;
            color: var(--color-text-primary);
            font-size: 16px;

            .material-icons {
                color: var(--color-primary);
            }
        }

        .toggle-btn {
            .material-icons {
                font-size: 18px;
                margin-left: 4px;
            }
        }
    }

    .attributes-content {
        .json-viewer-container {
            border-radius: 4px;
            overflow: hidden;
            
            .jsoneditor-container {
                border: 1px solid var(--color-border-default);
            }
        }
    }
}

.terminate-reason {
    padding: 16px;
    background: var(--color-danger-bg);
    border: 1px solid var(--color-danger-border);
    border-radius: 8px;

    .reason-header {
        margin-bottom: 12px;

        h5 {
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0;
            color: var(--color-danger);
            font-size: 16px;

            .material-icons {
                color: var(--color-danger);
            }
        }
    }

    .reason-content {
        .reason-text {
            font-size: 14px;
            color: var(--color-danger);
            font-weight: 600;
            font-family: 'Courier New', monospace;
        }
    }
}

.loading-container {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px;
    
    .loading-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        color: var(--color-text-secondary);
        
        .material-icons {
            font-size: 32px;
            color: var(--color-primary);
            
            &.rotating {
                animation: rotate 1s linear infinite;
            }
        }
        
        p {
            margin: 0;
            font-size: 16px;
        }
    }
}

@keyframes rotate {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.block-value-modal-content {
    min-height: 500px;
    
    .jsoneditor-container {
        min-height: 500px;
        border: 1px solid var(--color-border-default);
        border-radius: 6px;
    }
}

.diagram-modal-content {
    min-height: 400px;
    padding: 16px;
    background: var(--color-bg-subtle);
    border-radius: 8px;
}
</style>
